import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Clock, Target, Download, Filter, Calendar } from 'lucide-react';
import { useDashboard } from '@/hooks/useDashboard';
import { useBills } from '@/hooks/useBills';

const generateAdvancedMetrics = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  return months.map(month => ({
    month,
    revenue: Math.floor(Math.random() * 50000) + 30000,
    bills: Math.floor(Math.random() * 200) + 100,
    accuracy: 85 + Math.random() * 15,
    processingTime: 2 + Math.random() * 3,
    costs: Math.floor(Math.random() * 5000) + 2000,
    efficiency: 70 + Math.random() * 30
  }));
};

const generateSupplierMetrics = () => {
  return [
    { name: 'Energy Corp', value: 35, color: '#3b82f6' },
    { name: 'Water Utilities', value: 25, color: '#10b981' },
    { name: 'Gas Company', value: 20, color: '#f59e0b' },
    { name: 'Internet Co', value: 15, color: '#8b5cf6' },
    { name: 'Others', value: 5, color: '#ef4444' }
  ];
};

export const EnterpriseAnalyticsEngine: React.FC = () => {
  const [timeRange, setTimeRange] = useState('6months');
  const [metricType, setMetricType] = useState('revenue');
  const { stats } = useDashboard();
  const { bills } = useBills();

  const advancedMetrics = generateAdvancedMetrics();
  const supplierData = generateSupplierMetrics();

  const predictiveInsights = [
    {
      title: 'Revenue Forecast',
      prediction: '+12% next quarter',
      confidence: '89%',
      trend: 'up',
      description: 'Based on historical patterns and seasonal trends'
    },
    {
      title: 'Processing Efficiency',
      prediction: '15% improvement possible',
      confidence: '76%',
      trend: 'up',
      description: 'By optimizing AI model parameters'
    },
    {
      title: 'Cost Optimization',
      prediction: '$2,400 monthly savings',
      confidence: '92%',
      trend: 'down',
      description: 'Through automated workflow improvements'
    }
  ];

  const kpiData = [
    {
      title: 'Total Revenue',
      value: `$${(stats?.kpis?.totalRevenue || 0).toLocaleString()}`,
      change: '+12.5%',
      trend: 'up',
      icon: DollarSign
    },
    {
      title: 'Processing Rate',
      value: `${stats?.kpis?.processingRate || 0}%`,
      change: '+5.2%',
      trend: 'up',
      icon: TrendingUp
    },
    {
      title: 'Avg Process Time',
      value: '2.3 min',
      change: '-8.1%',
      trend: 'down',
      icon: Clock
    },
    {
      title: 'Accuracy Score',
      value: '94.2%',
      change: '+2.8%',
      trend: 'up',
      icon: Target
    }
  ];

  const exportReport = (format: string) => {
    console.log(`Exporting report in ${format} format`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Enterprise Analytics Engine</h2>
          <p className="text-muted-foreground">Advanced insights and predictive analytics</p>
        </div>
        <div className="flex gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="3months">Last 3 months</SelectItem>
              <SelectItem value="6months">Last 6 months</SelectItem>
              <SelectItem value="1year">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* KPI Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {kpiData.map((kpi) => (
          <Card key={kpi.title}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-muted-foreground">{kpi.title}</CardTitle>
                <kpi.icon className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">{kpi.value}</div>
              <div className={`text-sm font-medium ${
                kpi.trend === 'up' ? 'text-success' : 'text-warning'
              }`}>
                {kpi.change} from last period
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="trends" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="trends">Performance Trends</TabsTrigger>
          <TabsTrigger value="suppliers">Supplier Analytics</TabsTrigger>
          <TabsTrigger value="predictions">Predictive Insights</TabsTrigger>
          <TabsTrigger value="reports">Custom Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue & Volume Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={advancedMetrics}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Processing Efficiency</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={advancedMetrics}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line type="monotone" dataKey="accuracy" stroke="hsl(var(--success))" strokeWidth={3} />
                    <Line type="monotone" dataKey="efficiency" stroke="hsl(var(--secondary))" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cost Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={advancedMetrics}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="costs" fill="hsl(var(--accent))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Processing Time Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={advancedMetrics}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line type="monotone" dataKey="processingTime" stroke="hsl(var(--warning))" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Supplier Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={supplierData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({name, value}) => `${name}: ${value}%`}
                    >
                      {supplierData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Suppliers by Volume</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {supplierData.map((supplier, index) => (
                    <div key={supplier.name} className="flex items-center justify-between p-3 rounded border border-border">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: supplier.color }}
                        />
                        <span className="font-medium">{supplier.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{supplier.value}%</div>
                        <div className="text-sm text-muted-foreground">
                          {Math.floor(Math.random() * 100) + 50} bills
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="predictions" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {predictiveInsights.map((insight, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg">{insight.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-2xl font-bold text-primary">{insight.prediction}</div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Confidence: {insight.confidence}</Badge>
                      <Badge variant={insight.trend === 'up' ? 'default' : 'secondary'}>
                        {insight.trend === 'up' ? '↗️' : '↘️'} {insight.trend}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{insight.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Machine Learning Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">Pattern Recognition</h3>
                  <div className="space-y-3">
                    <div className="p-3 rounded bg-primary/10">
                      <div className="font-medium">Seasonal Billing Patterns</div>
                      <div className="text-sm text-muted-foreground">
                        Higher utility bills detected in Q4, suggesting seasonal usage patterns
                      </div>
                    </div>
                    <div className="p-3 rounded bg-success/10">
                      <div className="font-medium">Supplier Optimization</div>
                      <div className="text-sm text-muted-foreground">
                        Energy Corp shows 23% faster processing times than average
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold">Anomaly Detection</h3>
                  <div className="space-y-3">
                    <div className="p-3 rounded bg-warning/10">
                      <div className="font-medium">Processing Delays</div>
                      <div className="text-sm text-muted-foreground">
                        3 suppliers showing consistent 2x processing time increases
                      </div>
                    </div>
                    <div className="p-3 rounded bg-destructive/10">
                      <div className="font-medium">Quality Issues</div>
                      <div className="text-sm text-muted-foreground">
                        15% decrease in OCR accuracy for bills from Water Utilities
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Custom Report Builder</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Report Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="financial">Financial Summary</SelectItem>
                      <SelectItem value="operational">Operational Performance</SelectItem>
                      <SelectItem value="supplier">Supplier Analysis</SelectItem>
                      <SelectItem value="quality">Quality Metrics</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Time Period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="excel">Excel</SelectItem>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="powerpoint">PowerPoint</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3">
                  <Button className="gradient-primary">
                    Generate Report
                  </Button>
                  <Button variant="outline">
                    <Filter className="w-4 h-4 mr-2" />
                    Advanced Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Scheduled Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded border border-border">
                    <div>
                      <div className="font-medium">Monthly Financial Summary</div>
                      <div className="text-sm text-muted-foreground">Every 1st of month</div>
                    </div>
                    <Badge variant="secondary">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded border border-border">
                    <div>
                      <div className="font-medium">Weekly Operations Report</div>
                      <div className="text-sm text-muted-foreground">Every Monday</div>
                    </div>
                    <Badge variant="secondary">Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Report Templates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button variant="outline" className="w-full justify-start">
                    Executive Dashboard
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    Compliance Report
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    Performance Benchmarks
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    Cost Analysis
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};